import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { deleteReport as deleteReportApi } from 'api/reports';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface DeleteReportDialogProps extends AlertDialog.AlertDialogRootProps {
    reportId: number;
    onSuccess?: () => void;
}

const DeleteReportDialog = (props: DeleteReportDialogProps) => {
    const { children, reportId, onSuccess, ...rest } = props;
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const { success, error } = useToast();

    const { isPending, mutate: deleteReport } = useMutation({
        mutationFn: () => deleteReportApi(accountId, reportId),
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Failed to delete report');
        },
        onSuccess: () => {
            success('Deleted report successfully');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.reportsWithFilters(accountId),
                exact: false,
            });
            onSuccess?.();
        },
    });

    return (
        <AlertDialog.Root {...rest} type={'error'}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Delete report?</AlertDialog.Title>
                        <AlertDialog.Description>
                            You're about to delete this report. All saved filters will be lost and
                            all recurring emails will be deleted. This action cannot be undone.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                        <AlertDialog.Action disabled={isPending} onClick={() => deleteReport()}>
                            Delete
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const DeleteReportDialogTrigger = AlertDialog.Trigger;

const Root = DeleteReportDialog;
const Trigger = DeleteReportDialogTrigger;

export {
    DeleteReportDialog,
    DeleteReportDialogTrigger,
    //
    Root,
    Trigger,
};
